import type { StaticCanvas } from 'fabric';
import type {
  ArrayBinding,
  CanvasJSONLikeObject,
  FieldBinding,
  PolymorphicBinding,
  SimpleBinding
} from '@doer/frontend-components/creativity-editor';
import type { Binder, PieceBindingEntry, ResolvedTarget } from './binder';
import type { RoutedFormData } from '../../../../types/templates';
import { simpleBinder } from './simple-binder';
import { arrayBinder } from './array-binder';
import { polymorphicBinder } from './polymorphic-binder';

export type { Binder, PieceBindingEntry, ResolvedTarget };

const BINDERS = {
  simple: simpleBinder,
  array: arrayBinder,
  polymorphic: polymorphicBinder
} as const;

// Dispatchers. Consumers call these instead of switching on `kind` themselves.

export async function preMountAllCanvas(
  canvas: StaticCanvas,
  bindings: FieldBinding[],
  formData: RoutedFormData,
  resolutionId?: string
): Promise<void> {
  for (const binding of bindings) {
    switch (binding.kind) {
      case 'simple':
        await BINDERS.simple.preMountCanvas?.(canvas, binding as SimpleBinding, formData, resolutionId);
        break;
      case 'array':
        await BINDERS.array.preMountCanvas?.(canvas, binding as ArrayBinding, formData, resolutionId);
        break;
      case 'polymorphic':
        await BINDERS.polymorphic.preMountCanvas?.(canvas, binding as PolymorphicBinding, formData, resolutionId);
        break;
    }
  }
}

export function preMountAllJson(
  objects: CanvasJSONLikeObject[],
  bindings: FieldBinding[],
  formData: RoutedFormData,
  resolutionId?: string
): CanvasJSONLikeObject[] {
  let acc = objects;
  for (const binding of bindings) {
    switch (binding.kind) {
      case 'simple':
        acc = BINDERS.simple.preMountJson?.(acc, binding as SimpleBinding, formData, resolutionId) ?? acc;
        break;
      case 'array':
        acc = BINDERS.array.preMountJson?.(acc, binding as ArrayBinding, formData, resolutionId) ?? acc;
        break;
      case 'polymorphic':
        acc = BINDERS.polymorphic.preMountJson?.(acc, binding as PolymorphicBinding, formData, resolutionId) ?? acc;
        break;
    }
  }
  return acc;
}

export function resolveBindingTargets(
  binding: FieldBinding,
  formData: RoutedFormData,
  defaults: RoutedFormData,
  resolutionId?: string
): ResolvedTarget[] {
  switch (binding.kind) {
    case 'simple':
      return BINDERS.simple.resolveTargets(binding, formData, defaults, resolutionId);
    case 'array':
      return BINDERS.array.resolveTargets(binding, formData, defaults, resolutionId);
    case 'polymorphic':
      return BINDERS.polymorphic.resolveTargets(binding, formData, defaults, resolutionId);
  }
}

export function pieceMapEntries(binding: FieldBinding): Array<[string, PieceBindingEntry]> {
  switch (binding.kind) {
    case 'simple':
      return BINDERS.simple.pieceMapEntries(binding);
    case 'array':
      return BINDERS.array.pieceMapEntries(binding);
    case 'polymorphic':
      return BINDERS.polymorphic.pieceMapEntries(binding);
  }
}

export function computeNextFieldValue(
  entry: PieceBindingEntry,
  formData: RoutedFormData,
  newValue: string
): unknown {
  switch (entry.kind) {
    case 'simple':
      return BINDERS.simple.computeNextFieldValue(entry, formData, newValue);
    case 'array':
      return BINDERS.array.computeNextFieldValue(entry, formData, newValue);
    case 'polymorphic':
      return BINDERS.polymorphic.computeNextFieldValue(entry, formData, newValue);
  }
}
